import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaShoppingBag,
  FaSync,
  FaPhone,
  FaUser,
  FaTimes,
  FaBox,
  FaTruck,
  FaCreditCard,
  FaEuroSign,
  FaCalendarAlt,
  FaCheck
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './SasheOrders.css';

// Sashe status definitions
const SASHE_STATUSES = {
  1: { label: 'Nová', color: '#3b82f6', icon: '🆕' },
  2: { label: 'Zaplatená', color: '#10b981', icon: '💰' },
  3: { label: 'Pripravená na odoslanie', color: '#8b5cf6', icon: '📦' },
  4: { label: 'Čaká na vyzdvihnutie', color: '#f59e0b', icon: '🚚' },
  5: { label: 'Zrušená', color: '#ef4444', icon: '❌' },
  6: { label: 'Nevybavená', color: '#6b7280', icon: '⏸️' },
  7: { label: 'Doručená', color: '#22c55e', icon: '✅' },
};

const ESHOP_LABELS = {
  'monama': 'monama',
  'monama_svadobka': 'monama_svadobka',
};

const CARRIER_NAMES = {
  1: 'Slovenská pošta',
  2: 'Kuriér',
  3: 'Packeta',
  4: 'Osobný odber',
  5: 'Balíkobox / Na poštu',
};

const PAYMENT_NAMES = {
  1: 'Dobierka',
  2: 'Platba vopred',
  3: 'Hotovosť',
};

const formatEur = (v) => new Intl.NumberFormat('sk-SK', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
}).format(v || 0);

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });
};

const SasheOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [eshopFilter, setEshopFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState([]); // empty = all
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const params = { eshop: eshopFilter, days: 90 };
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      const resp = await api.get('/sashe/orders', { params });
      setOrders(resp.data);
    } catch (e) {
      toast.error('Chyba pri načítaní objednávok');
    }
  }, [eshopFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const resp = await api.get('/sashe/orders/stats', { params: { eshop: eshopFilter } });
      setStats(resp.data);
    } catch (e) {
      console.error('Stats error:', e);
    }
  }, [eshopFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchOrders(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchOrders, fetchStats]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const resp = await api.post('/sashe/orders/sync', { eshop: 'all', days: 60 });
      const { total_new, total_updated } = resp.data;
      toast.success(`Synchronizácia OK: ${total_new} nových, ${total_updated} aktualizovaných`);
      await Promise.all([fetchOrders(), fetchStats()]);
    } catch (e) {
      toast.error('Chyba pri synchronizácii');
    } finally {
      setSyncing(false);
    }
  };

  const toggleStatus = (statusCode) => {
    setStatusFilter(prev =>
      prev.includes(statusCode) ? prev.filter(s => s !== statusCode) : [...prev, statusCode]
    );
  };

  const StatusBadge = ({ code }) => {
    const cfg = SASHE_STATUSES[code];
    if (!cfg) return null;
    return (
      <span className="sashe-status-badge" style={{ background: `${cfg.color}25`, color: cfg.color, borderColor: `${cfg.color}55` }}>
        <span>{cfg.icon}</span>
        <span>{cfg.label}</span>
      </span>
    );
  };

  return (
    <div className="sashe-orders-page">
      <div className="sashe-orders-header">
        <div className="header-left">
          <h1><FaShoppingBag /> Sashe Objednávky</h1>
          {stats?.last_sync && (
            <span className="last-sync">Posledný sync: {formatDate(stats.last_sync)}</span>
          )}
        </div>
        <motion.button
          className="btn-sync"
          onClick={handleSync}
          disabled={syncing}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <FaSync className={syncing ? 'spinning' : ''} />
          {syncing ? 'Synchronizujem...' : 'Synchronizovať teraz'}
        </motion.button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="stats-grid">
          <motion.div className="stat-card week" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="stat-label">Tento týždeň</div>
            <div className="stat-main">{formatEur(stats.week.total)}</div>
            <div className="stat-sub">
              <span>{stats.week.count} obj.</span>
              <span>Bez dopravy: <strong>{formatEur(stats.week.without_shipping)}</strong></span>
              <span>Doprava: {formatEur(stats.week.shipping)}</span>
            </div>
          </motion.div>

          <motion.div className="stat-card month" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div className="stat-label">Tento mesiac</div>
            <div className="stat-main">{formatEur(stats.month.total)}</div>
            <div className="stat-sub">
              <span>{stats.month.count} obj.</span>
              <span>Bez dopravy: <strong>{formatEur(stats.month.without_shipping)}</strong></span>
              <span>Doprava: {formatEur(stats.month.shipping)}</span>
            </div>
          </motion.div>

          <motion.div className="stat-card status" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="stat-label">Stav (90 dní)</div>
            <div className="status-mini-list">
              {[1, 2, 3, 4, 6, 7, 5].map(code => {
                const count = stats.status_breakdown?.[code] || 0;
                if (count === 0) return null;
                const cfg = SASHE_STATUSES[code];
                return (
                  <div key={code} className="status-mini-item">
                    <span style={{ color: cfg.color }}>{cfg.icon}</span>
                    <span className="mini-label">{cfg.label}</span>
                    <span className="mini-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row">
        <div className="filter-group">
          <label>Eshop:</label>
          <div className="eshop-tabs">
            {[['all', 'Všetky'], ['monama', 'monama'], ['monama_svadobka', 'svadobka']].map(([key, lbl]) => (
              <button
                key={key}
                className={`eshop-tab ${eshopFilter === key ? 'active' : ''}`}
                onClick={() => setEshopFilter(key)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label>Stav:</label>
          <div className="status-chips">
            {Object.entries(SASHE_STATUSES).map(([code, cfg]) => {
              const c = parseInt(code);
              const active = statusFilter.includes(c);
              return (
                <button
                  key={code}
                  className={`status-chip ${active ? 'active' : ''}`}
                  onClick={() => toggleStatus(c)}
                  style={active ? { background: `${cfg.color}30`, color: cfg.color, borderColor: cfg.color } : {}}
                >
                  <span>{cfg.icon}</span>
                  <span>{cfg.label}</span>
                </button>
              );
            })}
            {statusFilter.length > 0 && (
              <button className="status-chip clear" onClick={() => setStatusFilter([])}>
                <FaTimes /> Zrušiť
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Orders list */}
      {loading ? (
        <div className="loading-state">Načítavam...</div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <FaShoppingBag />
          <p>Žiadne objednávky</p>
          <span>Skús kliknúť na "Synchronizovať teraz"</span>
        </div>
      ) : (
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Č. obj.</th>
                <th>Eshop</th>
                <th>Zákazník</th>
                <th>Stav</th>
                <th className="text-right">Suma</th>
                <th className="text-right">Bez dopravy</th>
                <th>Vytvorené</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {orders.map(order => (
                  <motion.tr
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setSelectedOrder(order)}
                    className="order-row"
                  >
                    <td className="mono">{order.order_number}</td>
                    <td><span className="eshop-pill">{ESHOP_LABELS[order.sashe_eshop] || order.sashe_eshop}</span></td>
                    <td>{order.customer_name}</td>
                    <td><StatusBadge code={order.sashe_status} /></td>
                    <td className="text-right amount">{formatEur(order.total_amount)}</td>
                    <td className="text-right amount-sub">{formatEur((order.total_amount || 0) - (order.total_shipping || 0))}</td>
                    <td className="created">{formatDate(order.created_at)}</td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {selectedOrder && (
        <div className="order-modal-bg" onClick={() => setSelectedOrder(null)}>
          <motion.div
            className="order-modal-card"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="modal-header">
              <h2>{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <FaUser />
                <span className="detail-label">Zákazník:</span>
                <span>{selectedOrder.customer_name}</span>
              </div>
              {selectedOrder.phone_number && (
                <div className="detail-row">
                  <FaPhone />
                  <span className="detail-label">Telefón:</span>
                  <a href={`tel:${selectedOrder.phone_number}`}>{selectedOrder.phone_number}</a>
                </div>
              )}
              <div className="detail-row">
                <FaBox />
                <span className="detail-label">Eshop:</span>
                <span className="eshop-pill">{ESHOP_LABELS[selectedOrder.sashe_eshop]}</span>
              </div>
              <div className="detail-row">
                <FaCheck />
                <span className="detail-label">Stav:</span>
                <StatusBadge code={selectedOrder.sashe_status} />
              </div>
              {selectedOrder.carrier_id && (
                <div className="detail-row">
                  <FaTruck />
                  <span className="detail-label">Doprava:</span>
                  <span>{CARRIER_NAMES[selectedOrder.carrier_id]} ({formatEur(selectedOrder.total_shipping)})</span>
                </div>
              )}
              {selectedOrder.payment_id && (
                <div className="detail-row">
                  <FaCreditCard />
                  <span className="detail-label">Platba:</span>
                  <span>{PAYMENT_NAMES[selectedOrder.payment_id]}</span>
                </div>
              )}
              {selectedOrder.tracking_code && (
                <div className="detail-row">
                  <FaTruck />
                  <span className="detail-label">Tracking:</span>
                  <span className="mono">{selectedOrder.tracking_code}</span>
                </div>
              )}
              <div className="detail-row">
                <FaCalendarAlt />
                <span className="detail-label">Vytvorené:</span>
                <span>{formatDate(selectedOrder.created_at)}</span>
              </div>

              <div className="totals-box">
                <div className="totals-row">
                  <span>Spolu s dopravou:</span>
                  <strong>{formatEur(selectedOrder.total_amount)}</strong>
                </div>
                <div className="totals-row">
                  <span>Bez dopravy:</span>
                  <span>{formatEur((selectedOrder.total_amount || 0) - (selectedOrder.total_shipping || 0))}</span>
                </div>
                <div className="totals-row sub">
                  <span>Doprava:</span>
                  <span>{formatEur(selectedOrder.total_shipping)}</span>
                </div>
              </div>

              {selectedOrder.description && (
                <div className="description-box">
                  <h4>Detail objednávky</h4>
                  <pre>{selectedOrder.description}</pre>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SasheOrders;
